import type { CrossMarketStatus } from '../../services/crossMarketApi';
import type { MarketScope } from '../../types/marketScope';
import CrossMarketKrSnapshot from './CrossMarketKrSnapshot';
import './MarketScope.css';

interface Props {
  status: CrossMarketStatus | null;
  scope: MarketScope;
  loading: boolean;
  error: string | null;
}

export default function CrossMarketSnapshot({ status, scope, loading, error }: Props) {
  if (loading && !status) return <div className="market-snapshot state-box">시장 상태 로딩 중...</div>;
  if (error && !status) return <div className="market-snapshot state-box error-state">API 오류: {error}</div>;
  if (!status) return <div className="market-snapshot state-box">데이터 없음</div>;

  const showUs = scope !== 'KR';
  const showKr = scope !== 'US';

  return (
    <section className="market-snapshot">
      <header>
        <div>
          <span className="market-kicker">Cross-Market Status</span>
          <h3>시장별 운영 기준</h3>
        </div>
        <div className="market-live-chips">
          {showUs && <LiveChip label={usLiveLabel(status)} active={isUsLive(status)} />}
          {showKr && <LiveChip label={krLiveLabel(status)} active={isKrLive(status)} />}
        </div>
      </header>
      <p className="market-warning">
        Stock live route: {status.safety.stock_live_route}
        {status.safety.stock_live_route === 'toss' ? ` · ${status.safety.toss_live_market}` : ''}
      </p>
      <div className="market-snapshot-grid">
        {showUs && <UsSnapshot data={status} />}
        {showKr && <CrossMarketKrSnapshot data={status} />}
      </div>
      {error && <p className="market-warning">최신 시장 상태 조회 실패: {error}</p>}
    </section>
  );
}

function UsSnapshot({ data }: { data: CrossMarketStatus }) {
  const us = data.markets.US;
  const live = us.live_watchlist;
  return (
    <article className="market-snapshot-card us">
      <CardHead code="US" title="미국 운영" mode={isUsLive(data) ? 'Toss Live Pilot' : 'Toss 후보 검증 · 주문 잠금'} />
      <MetricRow label="Live 후보" value={watchlistLabel(live)} />
      <MetricRow label="최근 관측 참고" value={`${us.provider_observations}행 / ${us.batch_count}배치`} />
      <MetricRow label="최근 배치" value={`${us.latest_batch_rows}행 · ${us.latest_session || '데이터 없음'}`} />
      <MetricRow label="참고 종목" value={us.symbols.join(', ') || '데이터 없음'} />
      <MetricRow label="상태" value={isUsLive(data) ? 'Toss US live_pilot 활성' : 'Toss US 주문 잠금'} />
    </article>
  );
}

function CardHead({ code, title, mode }: { code: string; title: string; mode: string }) {
  return (
    <div className="market-card-head">
      <span>{code}</span>
      <div>
        <strong>{title}</strong>
        <small>{mode}</small>
      </div>
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="market-metric-row">
      <small>{label}</small>
      <span>{value}</span>
    </div>
  );
}

function LiveChip({ label, active }: { label: string; active: boolean }) {
  return <span className={active ? 'live-chip danger' : 'live-chip'}>{label}</span>;
}

function isUsLive(data: CrossMarketStatus) {
  const safety = data.safety;
  return safety.trading_mode === 'live'
    && safety.live_trading_enabled
    && safety.toss_live_market === 'US'
    && safety.toss_us_live_enabled;
}

function isKrLive(data: CrossMarketStatus) {
  const safety = data.safety;
  return safety.trading_mode === 'live'
    && safety.live_trading_enabled
    && safety.toss_live_market === 'KR'
    && safety.toss_kr_live_enabled;
}

function usLiveLabel(data: CrossMarketStatus) {
  return isUsLive(data) ? 'US LIVE 주문 가능' : 'US LIVE 주문 잠금';
}

function krLiveLabel(data: CrossMarketStatus) {
  return isKrLive(data) ? 'KR LIVE 주문 가능' : 'KR LIVE 주문 잠금';
}

function watchlistLabel(watchlist: CrossMarketStatus['markets']['US']['live_watchlist']) {
  if (!watchlist) return '데이터 없음';
  const prefix = watchlist.ready ? 'Toss 검증' : `미검증(${watchlist.source || '없음'})`;
  const sample = watchlist.tickers.slice(0, 5).join(', ');
  const more = watchlist.count > 5 ? ` 외 ${watchlist.count - 5}` : '';
  return `${prefix} ${watchlist.count}개${sample ? ` · ${sample}${more}` : ''}`;
}
